import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Package, ArrowLeft } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back!");
      const from = location.state?.from?.pathname || "/admin";
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-emerald-50/60 px-6 py-16">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-8">
          <ArrowLeft size={14} /> Back to site
        </Link>

        <div className="bg-white border border-slate-200 rounded-3xl shadow-xl shadow-slate-900/5 p-9">
          <div className="flex items-center gap-3 mb-7">
            <img
              src="/logo-icon-transparent.png"
              alt="High Roller"
              className="h-12 w-auto"
            />
            <div>
              <div className="font-heading font-semibold text-slate-900">High Roller Admin</div>
              <div className="text-xs text-slate-500">Sign in to manage the catalog</div>
            </div>
          </div>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2 block">Email</label>
              <input
                type="email"
                required
                data-testid="login-input-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@healthbridge-pharma.com"
                className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2 block">Password</label>
              <input
                type="password"
                required
                data-testid="login-input-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-70 text-white rounded-full px-8 py-3.5 font-medium transition-colors shadow-md shadow-blue-600/20"
            >
              {submitting ? <><Loader2 size={18} className="animate-spin" /> Signing in…</> : "Sign in"}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          Authorized personnel only · High Roller Consumer Goods Trading
        </p>
      </div>
    </div>
  );
}
